import React, { useEffect, useState } from "react";
import { useCookies } from "react-cookie";
import { Box, Button, Modal } from "@mui/material";
import { Button as Btn, Typography } from "@material-ui/core";
import { Spinner } from "../../components";
import { PrimaryColor } from "../../shared/styles/colors";
import CloseIcon from "@mui/icons-material/Close";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AlertService from "../../service/AlertService";
import ToastMessages from "../../model/enum/toast-messages.enum";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import useStyles from "./styles";
import ApiService from "../../service/ApiService";
import { DatabaseEntities } from "../../model/enum/database-entities.enum";
import User from "../../model/user";
import Pathnames from "../../model/enum/Pathnames";
import Misc from "../../utils/Misc";

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "35rem",
  bgcolor: "background.paper",
  border: "none",
  borderRadius: "8px",
  boxShadow: 24,
  pt: 2,
  px: 4,
  pb: 3,
  maxHeight: "90vh", // Restrict maximum height to 80% of the viewport height
  overflowY: "auto", // Enable vertical scrolling
  "@media (max-width: 640px)": {
    width: "88%",
  },
};

interface Props {
  more: boolean; //used in userProfile
}

const UpdatePwdModal = ({ more }: Props) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const [cookies, setCookie, removeCookie] = useCookies(["session"]);

  const userId = localStorage.getItem("userId") || "";
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [viewCurrentPassword, setViewCurrentPassword] = useState(false);
  const [viewNewPassword, setViewNewPassword] = useState(false);
  const [viewConfirmNewPassword, setViewConfirmNewPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleCurrentPassword = (event: any) =>
    setCurrentPassword(event.target.value);
  const handleNewPassword = (event: any) => setNewPassword(event.target.value);
  const handleConfirmNewPassword = (event: any) =>
    setConfirmNewPassword(event.target.value);
  const handleViewCurrentPassword = () =>
    setViewCurrentPassword(!viewCurrentPassword);
  const handleViewNewPassword = () => setViewNewPassword(!viewNewPassword);
  const handleViewConfirmNewPassword = () =>
    setViewConfirmNewPassword(!viewConfirmNewPassword);

  const invalidInput = (): boolean => {
    return (
      Misc.isNullOrEmpty(currentPassword) ||
      Misc.isNullOrEmpty(newPassword) ||
      Misc.isNullOrEmpty(confirmNewPassword)
    );
  };

  const logout = async () => {
    setLoading(true);
    removeCookie("session");
    localStorage.clear();
    await ApiService.post<any>(`${DatabaseEntities.AUTH}/logout`, {});
    setLoading(false);
    window.open(Pathnames.LOGIN, "_self");
  };

  const submit = async () => {
    if (!Misc.validPasswordFormat(newPassword)) {
      AlertService.error(ToastMessages.INVALID_PASSWORD_FORMAT);
      return;
    }

    if (newPassword !== confirmNewPassword) {
      AlertService.error(ToastMessages.PASSWORDS_DO_NOT_MATCH);
      return true;
    }

    if (currentPassword === newPassword) {
      AlertService.warning(ToastMessages.PASSWORDS_EQUAL);
      return;
    }

    setLoading(true);

    const payload = {
      oldPassword: currentPassword,
      newPassword: newPassword,
    };

    await ApiService.put<User>(
      `${DatabaseEntities.USERS}/updatePassword/${userId}`,
      payload,
    )
      .then(() => {
        AlertService.success(ToastMessages.USER_PASSWORD_UPDATED);
        logout();
      })
      .catch((error) => {
        console.log(error);
        AlertService.error(`${error.message}`);
      })
      .finally(() => {
        setLoading(false);
      });

    handleClose();
  };

  useEffect(() => {
    const keyDownHandler = (event: {
      key: string;
      preventDefault: () => void;
    }) => {
      if (event.key === "Enter") {
        event.preventDefault();
        if (!invalidInput()) submit();
      }
    };

    document.addEventListener("keydown", keyDownHandler);

    return () => {
      document.removeEventListener("keydown", keyDownHandler);
    };
  });

  return (
    <div>
      {more ? (
        <Button onClick={handleOpen}>
          <MoreVertIcon sx={{ color: PrimaryColor }} />
        </Button>
      ) : (
        <Typography className={classes.changeButton} onClick={handleOpen}>
          Change password
        </Typography>
      )}
      {open && (
        <Modal
          open={open}
          onClose={handleClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
          sx={{ overflowY: "auto" }}
        >
          {loading ? (
            <Spinner />
          ) : (
            <Box sx={style}>
              <div className={classes.headerDiv}>
                <Typography className={classes.title}>
                  Change password
                </Typography>
                <Button onClick={handleClose} sx={{ color: "black" }}>
                  <CloseIcon />
                </Button>
              </div>
              <hr />
              <div className={classes.modalContainer}>
                <div className="form__group">
                  <div className={classes.inputDiv}>
                    <input
                      type={viewCurrentPassword ? "text" : "password"}
                      className={classes.emptyInput}
                      placeholder="**********"
                      name="current_password"
                      id="current_password"
                      required
                      value={currentPassword}
                      onChange={handleCurrentPassword}
                    />
                    <label className="form__label">Current password</label>
                    {viewCurrentPassword ? (
                      <Btn onClick={handleViewCurrentPassword}>
                        <VisibilityOffIcon />
                      </Btn>
                    ) : (
                      <Btn onClick={handleViewCurrentPassword}>
                        <VisibilityIcon />
                      </Btn>
                    )}
                  </div>
                </div>
                <div className="form__group">
                  <div className={classes.inputDiv}>
                    <input
                      type={viewNewPassword ? "text" : "password"}
                      className={classes.emptyInput}
                      placeholder="**********"
                      name="new_password"
                      id="new_password"
                      required
                      value={newPassword}
                      onChange={handleNewPassword}
                    />
                    <label className="form__label">New password</label>
                    {viewNewPassword ? (
                      <Btn onClick={handleViewNewPassword}>
                        <VisibilityOffIcon />
                      </Btn>
                    ) : (
                      <Btn onClick={handleViewNewPassword}>
                        <VisibilityIcon />
                      </Btn>
                    )}
                  </div>
                </div>
                <div className="form__group">
                  <div className={classes.inputDiv}>
                    <input
                      type={viewConfirmNewPassword ? "text" : "password"}
                      className={classes.emptyInput}
                      placeholder="**********"
                      name="confirm_new_password"
                      id="confirm_new_password"
                      required
                      value={confirmNewPassword}
                      onChange={handleConfirmNewPassword}
                    />
                    <label className="form__label">Confirm new password</label>
                    {viewConfirmNewPassword ? (
                      <Btn onClick={handleViewConfirmNewPassword}>
                        <VisibilityOffIcon />
                      </Btn>
                    ) : (
                      <Btn onClick={handleViewConfirmNewPassword}>
                        <VisibilityIcon />
                      </Btn>
                    )}
                  </div>
                </div>
                <div className={classes.passwordCriteria}>
                  <Typography>
                    New password must meet the following criteria:
                  </Typography>
                  <ul>
                    <li>At least 8 characters long</li>
                    <li>Contains at least one uppercase letter (e.g. A-Z)</li>
                    <li>Contains at least one lowercase letter (e.g. a-z)</li>
                    <li>Contains at least one number (e.g. 0-9)</li>
                    <li>
                      Contains at least one special character (e.g. !@#$%&*)
                    </li>
                  </ul>
                </div>
              </div>
              <div className={classes.buttonDiv}>
                <Btn
                  className={classes.button}
                  onClick={submit}
                  disabled={invalidInput()}
                >
                  Save
                </Btn>
              </div>
            </Box>
          )}
        </Modal>
      )}
    </div>
  );
};

export default UpdatePwdModal;
